import React, { useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { PlayIcon, StopCircleIcon, SearchIcon } from 'lucide-react';
import { beneficiaries, Beneficiary } from '../data/beneficiaries';
export function PermitManagement() {
  const [activeTab, setActiveTab] = useState<'applications' | 'apply' | 'tracking' | 'history' | 'startTrip'>('applications');
  const [currentStep, setCurrentStep] = useState(0);
  // Beneficiary search state
  const [beneficiarySearch, setBeneficiarySearch] = useState('');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary | null>(null);
  const filteredBeneficiaries = useMemo(() => {
    if (!beneficiarySearch.trim()) return [];
    const term = beneficiarySearch.toLowerCase();
    return beneficiaries.filter(b => b.name.toLowerCase().includes(term) || b.nid.includes(term) || b.phone.includes(term)).slice(0, 5);
  }, [beneficiarySearch]);
  // Apply Permit Form State
  const [applyForm, setApplyForm] = useState({
    hasOwnVessel: '',
    vesselType: '',
    blcNo: '',
    blcCertificate: '',
    permitOrganization: '',
    permitVessel: ''
  });
  // Start Trip Form State
  const [startTripForm, setStartTripForm] = useState({
    permitId: '',
    conservation: '',
    tripOrganization: '',
    tripVessel: '',
    driverName: '',
    driverPhone: '',
    startLocation: '',
    destination: '',
    startDate: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5),
    approvalStatus: 'Pending Verification'
  });
  // Approved permits data for Start Trip form
  const approvedPermits = [{
    permitId: 'PER-2024-002',
    name: 'Karim Sheikh',
    type: 'Honey Collection',
    zone: 'Sarankhola Range',
    duration: '14 days',
    hasOwnVessel: true,
    blcNo: 'BLC-2024-0045',
    vesselType: 'Trawler',
    blcOrganization: 'Sundarban Fisheries Ltd'
  }, {
    permitId: 'PER-2024-003',
    name: 'Fatema Begum',
    type: 'Wood Collection',
    zone: 'Khulna Range',
    duration: '10 days',
    hasOwnVessel: false,
    blcNo: '',
    vesselType: 'Boat',
    blcOrganization: ''
  }];
  // Get selected permit details for Start Trip
  const selectedPermit = approvedPermits.find(p => p.permitId === startTripForm.permitId);
  const applications = [{
    permitId: 'PER-2024-001',
    name: 'Rahim Uddin',
    type: 'Fishing',
    appliedDate: '2024-03-28',
    duration: '7 days',
    status: 'pending' as const
  }, {
    permitId: 'PER-2024-002',
    name: 'Karim Sheikh',
    type: 'Honey Collection',
    appliedDate: '2024-03-27',
    duration: '14 days',
    status: 'approved' as const
  }, {
    permitId: 'PER-2024-003',
    name: 'Fatema Begum',
    type: 'Wood Collection',
    appliedDate: '2024-03-26',
    duration: '10 days',
    status: 'approved' as const
  }, {
    permitId: 'PER-2024-004',
    name: 'Abdul Hakim',
    type: 'Fishing',
    appliedDate: '2024-03-25',
    duration: '5 days',
    status: 'rejected' as const
  }, {
    permitId: 'PER-2024-005',
    name: 'Jamal Hossain',
    type: 'Honey Collection',
    appliedDate: '2024-03-30',
    duration: '7 days',
    status: 'pending' as const
  }];
  const activeTrips = [{
    name: 'Rahim Uddin',
    vessel: 'Boat-123',
    zone: 'Chandpai Range',
    entryTime: '2024-03-30 06:00',
    elapsed: '4h 30m'
  }, {
    name: 'Karim Sheikh',
    vessel: 'Trawler-456',
    zone: 'Sarankhola Range',
    entryTime: '2024-03-30 05:30',
    elapsed: '5h 00m'
  }, {
    name: 'Fatema Begum',
    vessel: 'N/A',
    zone: 'Khulna Range',
    entryTime: '2024-03-30 07:15',
    elapsed: '3h 15m'
  }];
  const tripHistory = [{
    tripId: 'TRIP-2024-001',
    name: 'Abdul Hakim',
    vessel: 'Boat-789',
    zone: 'Chandpai Range',
    entry: '2024-03-29 06:00',
    exit: '2024-03-29 18:30',
    duration: '12h 30m',
    status: 'approved' as const
  }, {
    tripId: 'TRIP-2024-002',
    name: 'Jamal Hossain',
    vessel: 'Dinghy-234',
    zone: 'Satkhira Range',
    entry: '2024-03-28 05:45',
    exit: '2024-03-28 19:00',
    duration: '13h 15m',
    status: 'approved' as const
  }];
  const applicationColumns = [{
    key: 'permitId',
    label: 'Permit ID'
  }, {
    key: 'name',
    label: 'Beneficiary Name'
  }, {
    key: 'type',
    label: 'Type'
  }, {
    key: 'appliedDate',
    label: 'Applied Date'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-1401" />
  }];
  const historyColumns = [{
    key: 'tripId',
    label: 'Trip ID'
  }, {
    key: 'name',
    label: 'Beneficiary'
  }, {
    key: 'vessel',
    label: 'Vessel'
  }, {
    key: 'zone',
    label: 'Zone'
  }, {
    key: 'entry',
    label: 'Entry Time'
  }, {
    key: 'exit',
    label: 'Exit Time'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-1402" />
  }];
  const filters = [{
    key: 'type',
    label: 'Permit Type',
    options: ['Fishing', 'Honey Collection', 'Wood Collection']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Pending', 'Approved', 'Rejected']
  }, {
    key: 'station',
    label: 'Station',
    options: ['Chandpai', 'Katka', 'Dhangmari', 'Harbaria']
  }];
  const wizardSteps = [{
    title: 'Select Beneficiary',
    description: 'Search and select beneficiary'
  }, {
    title: 'Permit Details',
    description: 'Enter permit information'
  }, {
    title: 'Vessel Selection',
    description: 'Select vessel if required'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-1403">
      <div data-id="element-1404">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1405">
          Permit & Trip Management
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-1406">
          Manage permits and track forest trips
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-1407">
        <div className="flex gap-4" data-id="element-1408">
          <button onClick={() => setActiveTab('applications')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'applications' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1409">
            Permit Applications
          </button>
          <button onClick={() => setActiveTab('apply')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'apply' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1410">
            Apply for Permit
          </button>
          <button onClick={() => setActiveTab('tracking')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tracking' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1411">
            Trip Tracking ({activeTrips.length})
          </button>
          <button onClick={() => setActiveTab('history')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'history' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1412">
            Trip History
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'applications' && <DataTable columns={applicationColumns} data={applications} filters={filters} onView={row => console.log('View', row)} onEdit={row => console.log('Edit', row)} onApprove={row => alert(`Approved permit: ${row.permitId}`)} searchPlaceholder="Search permits..." data-id="element-1413" />}

      {activeTab === 'apply' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      alert('Permit application submitted successfully!');
      setCurrentStep(0);
      setActiveTab('applications');
    }} data-id="element-1414">
          {currentStep === 0 && <div className="space-y-4" data-id="element-1415">
              <div data-id="element-1416">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1417">
                  Search Beneficiary *
                </label>
                <div className="relative" data-id="element-1418">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-1419" />
                  <input type="text" placeholder="Enter NID, Name, or Phone" value={beneficiarySearch} onChange={e => {
              setBeneficiarySearch(e.target.value);
              if (selectedBeneficiary) setSelectedBeneficiary(null);
            }} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1420" />
                </div>
              </div>

              {/* Search Results */}
              {!selectedBeneficiary && beneficiarySearch.trim() && <div className="space-y-2" data-id="element-1421">
                  {filteredBeneficiaries.length > 0 ? filteredBeneficiaries.map((b, idx) => <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-[#1B5E20] transition-colors" data-id="element-1422">
                        <div className="flex items-center gap-4" data-id="element-1423">
                          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-xl" data-id="element-1424">
                            {b.photo}
                          </div>
                          <div className="flex-1" data-id="element-1425">
                            <h4 className="font-semibold text-gray-900" data-id="element-1426">
                              {b.name}
                            </h4>
                            <div className="text-sm text-gray-600 mt-0.5 flex gap-4" data-id="element-1427">
                              <span data-id="element-1428">NID: {b.nid}</span>
                              <span data-id="element-1429">Phone: {b.phone}</span>
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1430">
                              {b.profession} • {b.village}
                            </div>
                          </div>
                          <button type="button" onClick={() => {
                setSelectedBeneficiary(b);
                setBeneficiarySearch(b.name);
              }} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-1431">
                            Select
                          </button>
                        </div>
                      </div>) : <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center text-sm text-gray-500" data-id="element-1432">
                      No beneficiaries found matching "{beneficiarySearch}"
                    </div>}
                </div>}

              {/* Selected Beneficiary */}
              {selectedBeneficiary && <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-1433">
                  <div className="flex items-center gap-4" data-id="element-1434">
                    <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center text-2xl" data-id="element-1435">
                      {selectedBeneficiary.photo}
                    </div>
                    <div className="flex-1" data-id="element-1436">
                      <div className="flex items-center gap-2" data-id="element-1437">
                        <h4 className="font-semibold text-gray-900" data-id="element-1438">
                          {selectedBeneficiary.name}
                        </h4>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-1439">
                          Selected
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1 grid grid-cols-2 gap-1" data-id="element-1440">
                        <div data-id="element-1441">NID: {selectedBeneficiary.nid}</div>
                        <div data-id="element-1442">Phone: {selectedBeneficiary.phone}</div>
                        <div data-id="element-1443">Profession: {selectedBeneficiary.profession}</div>
                        <div data-id="element-1444">Village: {selectedBeneficiary.village}</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => {
              setSelectedBeneficiary(null);
              setBeneficiarySearch('');
            }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-1445">
                      Change
                    </button>
                  </div>
                </div>}
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-1446">
              <div data-id="element-1447">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1448">
                  Permit Type *
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1449">
                  <option value="" data-id="element-1450">Select Type</option>
                  <option value="fishing" data-id="element-1451">Fishing</option>
                  <option value="honey" data-id="element-1452">Honey Collection</option>
                  <option value="wood" data-id="element-1453">Wood Collection</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1454">
                <div data-id="element-1455">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1456">
                    Requested Duration *
                  </label>
                  <input type="number" placeholder="Days" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1457" />
                </div>
                <div data-id="element-1458">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1459">
                    Number of Crew
                  </label>
                  <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1460" />
                </div>
              </div>
              <div data-id="element-1461">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1462">
                  Forest Zone/Area *
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1463">
                  <option value="" data-id="element-1464">Select Zone</option>
                  <option value="chandpai" data-id="element-1465">Chandpai Range</option>
                  <option value="sarankhola" data-id="element-1466">Sarankhola Range</option>
                  <option value="khulna" data-id="element-1467">Khulna Range</option>
                  <option value="satkhira" data-id="element-1468">Satkhira Range</option>
                </select>
              </div>
              <div data-id="element-1469">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1470">
                  Purpose
                </label>
                <textarea rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1471"></textarea>
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-6" data-id="element-1472">
              <div data-id="element-1473">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1474">
                  Do you have your own vessel?
                </label>
                <div className="grid grid-cols-2 gap-4" data-id="element-1475">
                  <button type="button" onClick={() => setApplyForm({
              ...applyForm,
              hasOwnVessel: 'yes'
            })} className={`p-4 border rounded-lg text-left transition-colors ${applyForm.hasOwnVessel === 'yes' ? 'border-[#1B5E20] bg-green-50' : 'border-gray-300 hover:border-[#1B5E20]'}`} data-id="element-1476">
                    <div className="font-medium text-gray-900" data-id="element-1477">
                      Yes, I have my own vessel
                    </div>
                  </button>
                  <button type="button" onClick={() => setApplyForm({
              ...applyForm,
              hasOwnVessel: 'no'
            })} className={`p-4 border rounded-lg text-left transition-colors ${applyForm.hasOwnVessel === 'no' ? 'border-[#1B5E20] bg-green-50' : 'border-gray-300 hover:border-[#1B5E20]'}`} data-id="element-1478">
                    <div className="font-medium text-gray-900" data-id="element-1479">
                      No, I don't have my own vessel
                    </div>
                  </button>
                </div>
              </div>

              {applyForm.hasOwnVessel === 'yes' && <div className="space-y-4 animate-in fade-in slide-in-from-top-2" data-id="element-1480">
                  <div className="grid grid-cols-2 gap-4" data-id="element-1481">
                    <div data-id="element-1482">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1483">
                        Vessel Type *
                      </label>
                      <select value={applyForm.vesselType} onChange={e => setApplyForm({
                ...applyForm,
                vesselType: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1484">
                        <option value="" data-id="element-1485">Select Type</option>
                        <option value="Boat" data-id="element-1486">Boat</option>
                        <option value="Launch" data-id="element-1487">Launch</option>
                        <option value="Ship" data-id="element-1488">Ship</option>
                        <option value="Trawler" data-id="element-1489">Trawler</option>
                      </select>
                    </div>
                    <div data-id="element-1490">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1491">
                        BLC No. *
                      </label>
                      <input type="text" required value={applyForm.blcNo} onChange={e => setApplyForm({
                ...applyForm,
                blcNo: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1492" />
                    </div>
                  </div>
                  <div data-id="element-1493">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1494">
                      BLC Certificate *
                    </label>
                    <input type="file" accept="image/*,application/pdf" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1495" />
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800" data-id="element-1496">
                    Your vessel BLC will be verified. The trip must use this
                    registered vessel.
                  </div>
                </div>}

              {applyForm.hasOwnVessel === 'no' && <div className="space-y-4 animate-in fade-in slide-in-from-top-2" data-id="element-1497">
                  <div data-id="element-1498">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1499">
                      Vessel Type *
                    </label>
                    <select value={applyForm.vesselType} onChange={e => setApplyForm({
              ...applyForm,
              vesselType: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1500">
                      <option value="" data-id="element-1501">Select Type</option>
                      <option value="Boat" data-id="element-1502">Boat</option>
                      <option value="Launch" data-id="element-1503">Launch</option>
                      <option value="Ship" data-id="element-1504">Ship</option>
                      <option value="Trawler" data-id="element-1505">Trawler</option>
                    </select>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800" data-id="element-1506">
                    A vessel will be assigned during the trip. You only need to
                    specify the type of vessel required.
                  </div>
                </div>}
            </div>}
          {currentStep === 3 && <div className="space-y-6" data-id="element-1507">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1508">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1509">
                  Beneficiary
                </h4>
                <div className="text-sm" data-id="element-1510">
                  {selectedBeneficiary ? <div className="grid grid-cols-2 gap-2" data-id="element-1511">
                      <div data-id="element-1512">
                        <span className="text-gray-600" data-id="element-1513">Name:</span>{' '}
                        <span className="font-medium" data-id="element-1514">
                          {selectedBeneficiary.name}
                        </span>
                      </div>
                      <div data-id="element-1515">
                        <span className="text-gray-600" data-id="element-1516">NID:</span>{' '}
                        <span className="font-medium" data-id="element-1517">
                          {selectedBeneficiary.nid}
                        </span>
                      </div>
                      <div data-id="element-1518">
                        <span className="text-gray-600" data-id="element-1519">Phone:</span>{' '}
                        <span className="font-medium" data-id="element-1520">
                          {selectedBeneficiary.phone}
                        </span>
                      </div>
                      <div data-id="element-1521">
                        <span className="text-gray-600" data-id="element-1522">Profession:</span>{' '}
                        <span className="font-medium" data-id="element-1523">
                          {selectedBeneficiary.profession}
                        </span>
                      </div>
                    </div> : <span className="text-gray-500" data-id="element-1524">
                      No beneficiary selected
                    </span>}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1525">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1526">
                  Permit Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-1527">
                  <div data-id="element-1528">
                    <span className="text-gray-600" data-id="element-1529">Type:</span>{' '}
                    <span className="font-medium" data-id="element-1530">Fishing</span>
                  </div>
                  <div data-id="element-1531">
                    <span className="text-gray-600" data-id="element-1532">Duration:</span>{' '}
                    <span className="font-medium" data-id="element-1533">7 days</span>
                  </div>
                  <div data-id="element-1534">
                    <span className="text-gray-600" data-id="element-1535">Zone:</span>{' '}
                    <span className="font-medium" data-id="element-1536">Chandpai Range</span>
                  </div>
                  <div data-id="element-1537">
                    <span className="text-gray-600" data-id="element-1538">Crew:</span>{' '}
                    <span className="font-medium" data-id="element-1539">3 persons</span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1540">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1541">Vessel</h4>
                <div className="text-sm" data-id="element-1542">
                  {applyForm.hasOwnVessel === 'yes' ? <div className="space-y-1" data-id="element-1543">
                      <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mb-2" data-id="element-1544">
                        Own Vessel
                      </div>
                      <div data-id="element-1545">
                        <span className="text-gray-600" data-id="element-1546">Type:</span>{' '}
                        <span className="font-medium" data-id="element-1547">
                          {applyForm.vesselType || 'Not provided'}
                        </span>
                      </div>
                      <div data-id="element-1548">
                        <span className="text-gray-600" data-id="element-1549">BLC No:</span>{' '}
                        <span className="font-medium" data-id="element-1550">
                          {applyForm.blcNo || 'Not provided'}
                        </span>
                      </div>
                    </div> : applyForm.hasOwnVessel === 'no' ? <div className="space-y-1" data-id="element-1551">
                      <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mb-2" data-id="element-1552">
                        No Own Vessel
                      </div>
                      <div data-id="element-1553">
                        <span className="text-gray-600" data-id="element-1554">Vessel Type:</span>{' '}
                        <span className="font-medium" data-id="element-1555">
                          {applyForm.vesselType || 'Not provided'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-1556">
                        Vessel & organization will be assigned during the trip
                      </div>
                    </div> : <span className="text-gray-500" data-id="element-1557">No vessel selected</span>}
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'tracking' && <div className="space-y-4" data-id="element-1558">
          <div className="flex justify-end" data-id="element-1559">
            <button onClick={() => setActiveTab('startTrip')} className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-1560">
              <PlayIcon size={16} data-id="element-1561" />
              Start New Trip
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1562">
            {activeTrips.map((trip, idx) => <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1563">
                <div className="flex items-center justify-between mb-3" data-id="element-1564">
                  <h4 className="font-semibold text-gray-900" data-id="element-1565">{trip.name}</h4>
                  <div className="flex items-center gap-1" data-id="element-1566">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-1567"></div>
                    <span className="text-xs text-green-600 font-medium" data-id="element-1568">
                      In Forest
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600" data-id="element-1569">
                  <div data-id="element-1570">
                    <span className="font-medium" data-id="element-1571">Vessel:</span> {trip.vessel}
                  </div>
                  <div data-id="element-1572">
                    <span className="font-medium" data-id="element-1573">Zone:</span> {trip.zone}
                  </div>
                  <div data-id="element-1574">
                    <span className="font-medium" data-id="element-1575">Entry:</span> {trip.entryTime}
                  </div>
                  <div data-id="element-1576">
                    <span className="font-medium" data-id="element-1577">Elapsed:</span> {trip.elapsed}
                  </div>
                </div>
                <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50" data-id="element-1578">
                  <StopCircleIcon size={16} data-id="element-1579" />
                  End Trip
                </button>
              </div>)}
          </div>
        </div>}

      {activeTab === 'startTrip' && <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-3xl mx-auto" data-id="element-1580">
          <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4" data-id="element-1581">
            <div data-id="element-1582">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1583">
                Start Forest Trip
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-1584">
                Select an approved permit and provide trip details
              </p>
            </div>
            <div className="text-right" data-id="element-1585">
              <div className="text-sm text-gray-500" data-id="element-1586">
                Trip ID (Auto-generated)
              </div>
              <div className="font-mono font-bold text-lg text-[#1B5E20]" data-id="element-1587">
                TRP-
                {Math.floor(Math.random() * 10000).toString().padStart(4, '0')}
              </div>
            </div>
          </div>

          <form onSubmit={e => {
        e.preventDefault();
        alert('Trip started successfully! Status updated to Ongoing.');
        setActiveTab('tracking');
      }} className="space-y-6" data-id="element-1588">
            {/* Conservation Link */}
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1589">
              <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1590">
                Conservation Link (Optional)
              </h3>
              <div data-id="element-1591">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1592">
                  Link to Tour Conservation
                </label>
                <select value={startTripForm.conservation} onChange={e => setStartTripForm({
              ...startTripForm,
              conservation: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1593">
                  <option value="" data-id="element-1594">None</option>
                  <option value="Eco Tour - Mangrove Trail (Mongla Port Services)" data-id="element-1595">
                    Eco Tour - Mangrove Trail (Mongla Port Services)
                  </option>
                  <option value="Sundarban Eco Tour 2024 (Bay of Bengal Trading Co.)" data-id="element-1596">
                    Sundarban Eco Tour 2024 (Bay of Bengal Trading Co.)
                  </option>
                </select>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1597">
                  Only approved Tour-based conservations are shown
                </p>
              </div>
            </div>

            {/* Select Approved Permit */}
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1598">
              <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1599">
                Select Approved Permit *
              </h3>
              <div data-id="element-1600">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1601">
                  Permit
                </label>
                <select required value={startTripForm.permitId} onChange={e => setStartTripForm({
              ...startTripForm,
              permitId: e.target.value,
              tripOrganization: ''
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1602">
                  <option value="" data-id="element-1603">Select an approved permit</option>
                  {approvedPermits.map(p => <option key={p.permitId} value={p.permitId} data-id="element-1604">
                      {p.permitId} — {p.name} ({p.type})
                    </option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1605">
                  Only approved permits are shown
                </p>
              </div>

              {/* Permit Details Card */}
              {selectedPermit && <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-1606">
                  <div className="flex items-center justify-between" data-id="element-1607">
                    <h4 className="font-semibold text-gray-900" data-id="element-1608">
                      Permit Details
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-1609">
                      Approved
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-1610">
                    <div data-id="element-1611">
                      <span className="text-gray-600" data-id="element-1612">Beneficiary:</span>{' '}
                      <span className="font-medium" data-id="element-1613">{selectedPermit.name}</span>
                    </div>
                    <div data-id="element-1614">
                      <span className="text-gray-600" data-id="element-1615">Type:</span>{' '}
                      <span className="font-medium" data-id="element-1616">{selectedPermit.type}</span>
                    </div>
                    <div data-id="element-1617">
                      <span className="text-gray-600" data-id="element-1618">Zone:</span>{' '}
                      <span className="font-medium" data-id="element-1619">{selectedPermit.zone}</span>
                    </div>
                    <div data-id="element-1620">
                      <span className="text-gray-600" data-id="element-1621">Duration:</span>{' '}
                      <span className="font-medium" data-id="element-1622">
                        {selectedPermit.duration}
                      </span>
                    </div>
                    <div data-id="element-1623">
                      <span className="text-gray-600" data-id="element-1624">Vessel Type:</span>{' '}
                      <span className="font-medium" data-id="element-1625">
                        {selectedPermit.vesselType}
                      </span>
                    </div>
                    <div data-id="element-1626">
                      <span className="text-gray-600" data-id="element-1627">BLC No:</span>{' '}
                      <span className="font-medium" data-id="element-1628">
                        {selectedPermit.blcNo || 'N/A (No own vessel)'}
                      </span>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Organization Section - conditional based on permit */}
            {selectedPermit && <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1629">
                <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1630">
                  Organization
                </h3>
                {selectedPermit.hasOwnVessel && selectedPermit.blcNo ? <div data-id="element-1631">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1632">
                      Organization (Auto-populated from BLC)
                    </label>
                    <input type="text" readOnly value={selectedPermit.blcOrganization} className="w-full px-3 py-2 border border-gray-200 bg-gray-100 rounded-lg text-gray-600" data-id="element-1633" />
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 mt-3" data-id="element-1634">
                      Organization auto-determined from BLC No.{' '}
                      <span className="font-medium" data-id="element-1635">
                        {selectedPermit.blcNo}
                      </span>
                    </div>
                  </div> : <div data-id="element-1636">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1637">
                      Select Organization *
                    </label>
                    <select required value={startTripForm.tripOrganization} onChange={e => setStartTripForm({
              ...startTripForm,
              tripOrganization: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1638">
                      <option value="" data-id="element-1639">Select Organization</option>
                      <option value="Sundarban Fisheries Ltd" data-id="element-1640">
                        Sundarban Fisheries Ltd
                      </option>
                      <option value="Bay of Bengal Trading Co." data-id="element-1641">
                        Bay of Bengal Trading Co.
                      </option>
                      <option value="Khulna Honey Producers" data-id="element-1642">
                        Khulna Honey Producers
                      </option>
                      <option value="Mongla Port Services" data-id="element-1643">
                        Mongla Port Services
                      </option>
                      <option value="Satkhira Wood Industries" data-id="element-1644">
                        Satkhira Wood Industries
                      </option>
                    </select>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mt-3" data-id="element-1645">
                      This permit has no BLC certificate. Please select the
                      organization providing the vessel.
                    </div>
                  </div>}
              </div>}

            {/* Driver & Journey Details */}
            {selectedPermit && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1646">
                {/* Driver Info */}
                <div className="space-y-4" data-id="element-1647">
                  <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1648">
                    Driver / Majhi Info
                  </h3>
                  <div data-id="element-1649">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1650">
                      Driver/Majhi Name *
                    </label>
                    <input type="text" required value={startTripForm.driverName} onChange={e => setStartTripForm({
                ...startTripForm,
                driverName: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1651" />
                  </div>
                  <div data-id="element-1652">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1653">
                      Driver Phone
                    </label>
                    <input type="tel" value={startTripForm.driverPhone} onChange={e => setStartTripForm({
                ...startTripForm,
                driverPhone: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1654" />
                  </div>
                </div>

                {/* Journey Details */}
                <div className="space-y-4" data-id="element-1655">
                  <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1656">
                    Journey Details
                  </h3>
                  <div data-id="element-1657">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1658">
                      Start Location (Station) *
                    </label>
                    <select required value={startTripForm.startLocation} onChange={e => setStartTripForm({
                ...startTripForm,
                startLocation: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1659">
                      <option value="" data-id="element-1660">Select Station</option>
                      <option value="chandpai" data-id="element-1661">Chandpai Station</option>
                      <option value="katka" data-id="element-1662">Katka Station</option>
                      <option value="dhangmari" data-id="element-1663">Dhangmari Station</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1664">
                    <div data-id="element-1665">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1666">
                        Start Date *
                      </label>
                      <input type="date" required value={startTripForm.startDate} onChange={e => setStartTripForm({
                  ...startTripForm,
                  startDate: e.target.value
                })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1667" />
                    </div>
                    <div data-id="element-1668">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1669">
                        Start Time *
                      </label>
                      <input type="time" required value={startTripForm.startTime} onChange={e => setStartTripForm({
                  ...startTripForm,
                  startTime: e.target.value
                })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1670" />
                    </div>
                  </div>
                </div>
              </div>}

            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200" data-id="element-1671">
              <button type="button" onClick={() => setActiveTab('tracking')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium" data-id="element-1672">
                Cancel
              </button>
              <button type="submit" disabled={!selectedPermit} className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1673">
                <PlayIcon size={18} data-id="element-1674" />
                Start Trip
              </button>
            </div>
          </form>
        </div>}

      {activeTab === 'history' && <DataTable columns={historyColumns} data={tripHistory} onView={row => console.log('View', row)} searchPlaceholder="Search trip history..." data-id="element-1675" />}
    </div>;
}